import React, {
  memo, useEffect, useRef, useState,
} from 'react';

import {
  TILE_CSS_H,
  TILE_CSS_W,
  tileBoxesKey,
  tileKey,
} from '../helpers';
import style from './style.module.scss';
import MosaicCanvas from './MosaicCanvas';
import TileView from './TileView';
import { useMapView } from './Map';

/**
 * Canvas mosaic + DOM hit targets (cols×TILE_CSS_W × rows×TILE_CSS_H).
 * Clicks use percentage-positioned buttons — same coords as painted boxes.
 */
const Grid = ({
  scan,
  tiles,
  rows,
  cols,
  tileBoxes,
  selectedLabels,
  showBoxes,
  onOpenDetail,
  loading = false,
  boxKeyPrefix = null,
  scanKey = '',
  variant = 'plain',
  slotWidth = 0,
  slotHeight = 0,
  overlay = null,
}) => {
  const r = Number(rows) || 0;
  const c = Number(cols) || 0;
  const list = tiles || [];
  const empty = !loading && !list.length && r > 0 && c > 0;
  const gridClass = variant === 'slot' ? style.stadiumSlotGrid : style.grid;
  const showCanvas = r > 0 && c > 0 && (loading || list.length > 0);
  const slot = variant === 'slot';
  const { gesturing, viewportEl } = useMapView();
  const gridRef = useRef(null);
  const [visibleKeys, setVisibleKeys] = useState(() => new Set());

  const natW = c * TILE_CSS_W;
  const natH = r * TILE_CSS_H;
  const shellStyle = (c > 0 && r > 0) ? {
    width: natW,
    height: natH,
    ...(slot && slotWidth > 0 && slotHeight > 0 ? {
      transform: `scale(${slotWidth / natW}, ${slotHeight / natH})`,
      transformOrigin: '0 0',
    } : null),
  } : undefined;

  // Viewport culling for stadium slots; single-region tiles are always active.
  useEffect(() => {
    if (!slot) return undefined;
    const root = gridRef.current;
    if (!root || loading || empty) return undefined;

    const visible = new Set();
    const io = new IntersectionObserver(
      (entries) => {
        let changed = false;
        entries.forEach((entry) => {
          const key = entry.target.dataset.tileKey;
          if (!key) return;
          if (entry.isIntersecting) {
            if (!visible.has(key)) { visible.add(key); changed = true; }
          } else if (visible.delete(key)) {
            changed = true;
          }
        });
        if (changed) setVisibleKeys(new Set(visible));
      },
      { root: viewportEl || null, rootMargin: '25%', threshold: 0 },
    );

    root.querySelectorAll('[data-tile-key]').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slot, loading, empty, list, viewportEl, boxKeyPrefix]);

  const grid = (
    <div className={style.gridShell} style={shellStyle}>
      {showCanvas ? (
        <MosaicCanvas
          tiles={list}
          rows={r}
          cols={c}
          loading={loading}
          tileBoxes={tileBoxes}
          selectedLabels={selectedLabels}
          boxKeyPrefix={boxKeyPrefix}
          scanKey={scanKey}
          viewportLoad={slot}
          overlay={overlay}
        />
      ) : null}

      {empty ? (
        <div
          className={`${gridClass} ${style.gridOverlay}`}
          style={{ gridTemplateColumns: `repeat(${c}, ${TILE_CSS_W}px)` }}
        >
          <output className={style.gridEmpty}>
            No tiles for this region
          </output>
        </div>
      ) : null}

      {!loading && !empty ? (
        <div
          ref={gridRef}
          className={`${gridClass} ${style.gridOverlay}`}
          style={{
            gridTemplateColumns: `repeat(${c}, ${TILE_CSS_W}px)`,
            gridTemplateRows: `repeat(${r}, ${TILE_CSS_H}px)`,
          }}
        >
          {list.map((tile) => {
            const key = tileKey(tile);
            const reactKey = boxKeyPrefix != null ? `${boxKeyPrefix}:${key}` : key;
            const active = slot
              ? (!gesturing && visibleKeys.has(reactKey))
              : !gesturing;
            return (
              <TileView
                key={reactKey}
                tileKey={reactKey}
                tile={boxKeyPrefix ? { ...tile, _scanId: boxKeyPrefix } : tile}
                scan={scan}
                boxes={tileBoxes?.[tileBoxesKey(tile, boxKeyPrefix)] || []}
                selectedLabels={selectedLabels}
                showBoxes={showBoxes && active}
                onOpenDetail={onOpenDetail}
                boxKeyPrefix={boxKeyPrefix}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );

  if (!slot) return grid;
  return <div className={style.stadiumSlotClip}>{grid}</div>;
};

export default memo(Grid);
